import { call, cancelled, put, select, takeEvery } from 'redux-saga/effects';
import { getArticle } from 'common/selectors';
import { EffectR } from 'types/utils';
import { api } from 'api';
import { ActionType, createAction, createAsyncAction, createReducer } from 'typesafe-actions';
import { MerchantInfo } from '../types';
import { fetchProductMerchantInfo } from './api';

export const GET_PRODUCT_MERCHANT_INFO = createAction('ui/GET_PRODUCT_MERCHANT_INFO')<{
  regionId: string;
}>();

export const FETCH_PRODUCT_MERCHANT_INFO = createAsyncAction(
  'product/FETCH_PRODUCT_MERCHANT_INFO/request',
  'product/FETCH_PRODUCT_MERCHANT_INFO/success',
  'product/FETCH_PRODUCT_MERCHANT_INFO/failure',
  'product/FETCH_PRODUCT_MERCHANT_INFO/cancel',
)<void, MerchantInfo, Response | Error, void>();

export const merchantInfo = createReducer<MerchantInfo | null>(null)
  .handleAction([FETCH_PRODUCT_MERCHANT_INFO.failure, FETCH_PRODUCT_MERCHANT_INFO.cancel], () => null)
  .handleAction(FETCH_PRODUCT_MERCHANT_INFO.success, (_, { payload }) => payload);

export const merchantError = createReducer<Response | Error | null>(null).handleAction(
  [FETCH_PRODUCT_MERCHANT_INFO.failure],
  (_, { payload }) => payload,
);

export const merchantLoading = createReducer(false)
  .handleAction([FETCH_PRODUCT_MERCHANT_INFO.request], () => true)
  .handleAction(
    [
      FETCH_PRODUCT_MERCHANT_INFO.success,
      FETCH_PRODUCT_MERCHANT_INFO.failure,
      FETCH_PRODUCT_MERCHANT_INFO.cancel,
    ],
    () => false,
  );

export function* runGetProductMerchantInfo() {
  yield takeEvery(
    GET_PRODUCT_MERCHANT_INFO,
    function* handle({ payload }: ActionType<typeof GET_PRODUCT_MERCHANT_INFO>) {
      const { regionId } = payload;
      yield doFetchProductMerchantInfo(regionId);
    },
  );
}

export function* doFetchProductMerchantInfo(regionId: string) {
  const productId: string = yield select(getArticle);

  const abortController = (yield call([api, 'abortController'])) as EffectR.Call<
    (typeof api)['abortController']
  >;

  yield put(FETCH_PRODUCT_MERCHANT_INFO.request());

  try {
    const data = (yield call(
      fetchProductMerchantInfo,
      productId,
      regionId,
      abortController.token,
    )) as EffectR.Call<typeof fetchProductMerchantInfo>;

    yield put(FETCH_PRODUCT_MERCHANT_INFO.success(data));
  } catch (e) {
    if (e instanceof Error) {
      yield put(FETCH_PRODUCT_MERCHANT_INFO.failure(e));
    }
    // eslint-disable-next-line no-console
    console.error('Cannot load merchant info', e);
  } finally {
    if ((yield cancelled()) as boolean) {
      abortController.cancel();
      yield put(FETCH_PRODUCT_MERCHANT_INFO.cancel());
    }
  }
}
